import React, { useState, useEffect, useRef } from 'react';
import { Feedback as FeedbackType, FeedbackThread as FeedbackThreadType } from '../../types/artist';
import { FeedbackThreadInline } from './FeedbackThreadInline';
import { ReportModal } from './ReportModal';
import { feedbackThreadApi } from '../../services/feedbackThreadApi';
import { feedbackApi } from '../../services/feedbackApi';
import { useAuth } from '../../contexts/AuthContext';
import './Feedback.css';

interface FeedbackProps {
  feedback: FeedbackType;
  showAuthor?: boolean;
  variant?: 'list' | 'card';
  className?: string;
  showThreadButton?: boolean;
}

export const Feedback: React.FC<FeedbackProps> = ({
  feedback,
  showAuthor = true,
  variant = 'list',
  className = '',
  showThreadButton = true,
}) => {
  const { user } = useAuth();
  const [threadData, setThreadData] = useState<FeedbackThreadType | null>(feedback.thread || null);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Determine if current user is the reviewer
  const isReviewer = user?.id === feedback.createdByUserId;
  
  // Don't show report option for user's own feedback
  const canReport = !isReviewer && feedback.id !== undefined;

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMoreMenu(false);
      }
    };

    if (showMoreMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMoreMenu]);

  // Load thread data when component mounts if feedback has an ID
  useEffect(() => {
    const loadThreadData = async () => {
      if (feedback.id && !threadData && showThreadButton) {
        try {
          const thread = await feedbackThreadApi.getThread(feedback.id);
          setThreadData(thread);
        } catch (err) {
          console.error('Failed to load thread data:', err);
          // Thread might not exist yet, which is fine
        }
      }
    };

    loadThreadData();
  }, [feedback.id, threadData, showThreadButton]);

  // Format time from seconds to mm:ss
  const formatTime = (seconds: number): string => {
    // Handle invalid or non-finite values
    if (!isFinite(seconds) || isNaN(seconds) || seconds < 0) {
      return '0:00';
    }
    
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const containerClass = `feedback-component ${variant === 'card' ? 'feedback-card' : 'feedback-item'} ${className}`;

  const handleReportFeedback = async (reason: string): Promise<void> => {
    if (!feedback.id) {
      throw new Error('No feedback ID to report');
    }
    await feedbackApi.reportFeedback(feedback.id, { reason });
  };

  const handleReportClick = () => {
    setShowMoreMenu(false);
    setShowReportModal(true);
  };

  return (
    <div className={containerClass}>
      <div className="feedback-header">
        <div className="feedback-header-left">
          {showAuthor && (
            <span className={variant === 'card' ? 'feedback-author-hidden' : 'feedback-author'}>
              Anonymous Reviewer
            </span>
          )}
          <span className="feedback-date">
            {variant === 'card' 
              ? new Date(feedback.createdAt).toLocaleString()
              : new Date(feedback.createdAt).toLocaleDateString()
            }
          </span>
        </div>
        {canReport && (
          <div className="feedback-more-menu-container" ref={menuRef}>
            <button
              className="feedback-more-menu-button"
              onClick={() => setShowMoreMenu(!showMoreMenu)}
              aria-label="More options"
            >
              ⋯
            </button>
            {showMoreMenu && (
              <div className="feedback-more-menu-dropdown">
                <button
                  className="feedback-more-menu-item report"
                  onClick={handleReportClick}
                >
                  🚩 Report
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="feedback-ratings">
        <div className="rating-item">
          <span className={variant === 'card' ? 'rating-label' : 'rating-span'}>Overall:</span>
          <span className={variant === 'card' ? 'rating-value' : 'rating'}>⭐ {feedback.overallRating}/10</span>
        </div>
        <div className="rating-item">
          <span className={variant === 'card' ? 'rating-label' : 'rating-span'}>Composition:</span>
          <span className={variant === 'card' ? 'rating-value' : 'rating'}>⭐ {feedback.compositionRating}/10</span>
        </div>
        <div className="rating-item">
          <span className={variant === 'card' ? 'rating-label' : 'rating-span'}>Music Quality:</span>
          <span className={variant === 'card' ? 'rating-value' : 'rating'}>⭐ {feedback.musicQualityRating}/10</span>
        </div>
        <div className="rating-item">
          <span className={variant === 'card' ? 'rating-label' : 'rating-span'}>Production:</span>
          <span className={variant === 'card' ? 'rating-value' : 'rating'}>⭐ {feedback.productionQualityRating}/10</span>
        </div>
      </div>

      {feedback.review && (
        <div className={variant === 'card' ? 'feedback-review' : 'feedback-comments'}>
          <h5>Review:</h5>
          <p>{feedback.review}</p>
        </div>
      )}

      {feedback.timestampedNotes && feedback.timestampedNotes.length > 0 && (
        <div className="timestamped-notes">
          <h5>Timestamped Notes:</h5>
          <div className="notes-list">
            {feedback.timestampedNotes.map(note => (
              <div key={note.id} className="note-item">
                <span className="note-timestamp">
                  {formatTime(note.timestampInSeconds)}
                </span>
                <span className="note-text">{note.note}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Inline Thread */}
      {showThreadButton && (
        <FeedbackThreadInline
          feedback={feedback}
          isReviewer={isReviewer}
        />
      )}

      {/* Report Modal */}
      {showReportModal && (
        <ReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          onSubmit={handleReportFeedback}
          title="Report Content Violation"
          itemName="this feedback"
          itemType="feedback"
        />
      )}
    </div>
  );
};